import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { SignalData, SignalCard } from './SignalCard';

interface Props {
    session: Session;
}

interface StrategyStats {
    strategy_name: string;
    scanner_type: string;
    total_trades: number;
    wins: number;
    losses: number;
    win_rate: number;
    total_rr: number;
}

const Strategies: React.FC<Props> = ({ session }) => {
    const [loading, setLoading] = useState(true);
    const [globalStats, setGlobalStats] = useState({ totalActive: 0, totalWins: 0, totalLosses: 0, globalWinRate: 0, globalRR: 0 });
    const [strategyStats, setStrategyStats] = useState<StrategyStats[]>([]);
    const [history, setHistory] = useState<SignalData[]>([]);
    const [activeTab, setActiveTab] = useState<'analytics' | 'history'>('analytics');

    const [filterScanner, setFilterScanner] = useState<'all' | '313' | 'elite_53'>('all');

    useEffect(() => {
        const fetchAnalytics = async () => {
            // 1. Fetch History (Win/Loss)
            const { data: signals } = await supabase
                .from('signals')
                .select('*')
                .order('created_at', { ascending: false });

            if (signals) {
                let sigs = signals as SignalData[];

                if (filterScanner !== 'all') {
                    sigs = sigs.filter(s => s.scanner_type === filterScanner);
                }

                // Split for tabs
                const completedSigs = sigs.filter(s => s.result === 'win' || s.result === 'loss');
                setHistory(completedSigs);

                // Global Stats
                const wins = completedSigs.filter(s => s.result === 'win').length;
                const losses = completedSigs.filter(s => s.result === 'loss').length;
                const totalCompleted = wins + losses;
                const globalWinRate = totalCompleted > 0 ? Math.round((wins / totalCompleted) * 100) : 0;

                const globalRR = completedSigs.reduce((acc, curr) => {
                    if (curr.result === 'win') return acc + 2.5; // Robust 1:2.5 default model
                    if (curr.result === 'loss') return acc - 1.0;
                    return acc;
                }, 0);

                setGlobalStats({
                    totalActive: sigs.filter(s => s.result === 'active' || s.result === 'pending').length,
                    totalWins: wins,
                    totalLosses: losses,
                    globalWinRate,
                    globalRR: Math.round(globalRR * 100) / 100
                });

                // specific strategy tracking
                const statMap: Record<string, StrategyStats> = {};

                completedSigs.forEach(sig => {
                    const key = sig.strategy_name || 'Unknown Strategy';
                    if (!statMap[key]) {
                        statMap[key] = { strategy_name: key, scanner_type: sig.scanner_type, total_trades: 0, wins: 0, losses: 0, win_rate: 0, total_rr: 0 };
                    }
                    statMap[key].total_trades += 1;
                    if (sig.result === 'win') {
                        statMap[key].wins += 1;
                        statMap[key].total_rr += 2.5;
                    } else if (sig.result === 'loss') {
                        statMap[key].losses += 1;
                        statMap[key].total_rr -= 1.0;
                    }
                });

                // Compute win rates and sort
                const statsArray = Object.values(statMap).map(st => ({
                    ...st,
                    win_rate: Math.round((st.wins / st.total_trades) * 100)
                })).sort((a, b) => b.total_rr - a.total_rr);

                setStrategyStats(statsArray);
            }
            setLoading(false);
        };

        fetchAnalytics();
    }, [session, filterScanner]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-finance-darker"><div className="w-8 h-8 border-4 border-finance-accent border-r-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">

            {/* Page Header */}
            <div className="mb-10 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-2">Global Signal Analytics</h1>
                    <p className="text-gray-400 max-w-2xl">Complete transparency. Track the live, unimpeachable performance record of our Institutional AI Quant Models.</p>
                </div>
                <div>
                    <select
                        value={filterScanner}
                        onChange={(e) => setFilterScanner(e.target.value as 'all' | '313' | 'elite_53')}
                        className="bg-[#0b0f19] text-white border border-gray-800 rounded-lg px-4 py-2 focus:border-finance-accent focus:ring-1 focus:ring-finance-accent transition-all"
                    >
                        <option value="all">Overall Performance (All Sets)</option>
                        <option value="elite_53">Elite Quant Model (53)</option>
                        <option value="313">Standard Model (313)</option>
                    </select>
                </div>
            </div>

            {/* Global KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <div className="bg-[#0b0f19] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-finance-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    </div>
                    <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">Global Win Rate</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-black text-white">{globalStats.globalWinRate}%</span>
                    </div>
                </div>

                <div className="bg-[#0b0f19] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-finance-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">Total R:R Gained</p>
                    <div className="flex items-baseline gap-2">
                        <span className={`text-4xl font-black ${globalStats.globalRR >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {globalStats.globalRR > 0 ? '+' : ''}{globalStats.globalRR}R
                        </span>
                    </div>
                </div>

                <div className="bg-[#0b0f19] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group">
                    <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">Total Trades</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-black text-white">{globalStats.totalWins + globalStats.totalLosses}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 font-medium">
                        <span className="text-green-400">{globalStats.totalWins} W</span> / <span className="text-red-400">{globalStats.totalLosses} L</span>
                    </p>
                </div>

                <div className="bg-[#0b0f19] border border-gray-800/80 rounded-2xl p-6 relative overflow-hidden group">
                    <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">Active Setups</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-black text-blue-400">{globalStats.totalActive}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 font-medium flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                        Currently Monitoring
                    </p>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-800 mb-8">
                <button
                    onClick={() => setActiveTab('analytics')}
                    className={`px-6 py-4 font-bold text-sm tracking-widest uppercase transition-colors border-b-2 ${activeTab === 'analytics' ? 'border-finance-accent text-finance-accent' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    Model Performance Breakdown
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-6 py-4 font-bold text-sm tracking-widest uppercase transition-colors border-b-2 ${activeTab === 'history' ? 'border-finance-accent text-finance-accent' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                >
                    Raw Signal History
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'analytics' && (
                <div className="bg-[#0b0f19] border border-gray-800 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-900/50 border-b border-gray-800">
                                    <th className="p-4 text-xs font-black text-gray-400 tracking-wider uppercase">Strategy Name</th>
                                    <th className="p-4 text-xs font-black text-gray-400 tracking-wider uppercase text-center">Class</th>
                                    <th className="p-4 text-xs font-black text-gray-400 tracking-wider uppercase text-right">Trades</th>
                                    <th className="p-4 text-xs font-black text-gray-400 tracking-wider uppercase text-right">Win Rate</th>
                                    <th className="p-4 text-xs font-black text-gray-400 tracking-wider uppercase text-right">Net R:R</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800/60">
                                {strategyStats.length === 0 ? (
                                    <tr><td colSpan={5} className="p-8 text-center text-gray-500">No trading data available yet.</td></tr>
                                ) : (
                                    strategyStats.map((stat, i) => (
                                        <tr key={i} className="hover:bg-gray-800/30 transition-colors">
                                            <td className="p-4 font-medium text-gray-300">{stat.strategy_name}</td>
                                            <td className="p-4 text-center">
                                                <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wider ${stat.scanner_type === 'elite_53' ? 'bg-finance-accent/10 text-finance-accent border border-finance-accent/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                    }`}>
                                                    {stat.scanner_type === 'elite_53' ? 'Elite' : 'Basic'}
                                                </span>
                                            </td>
                                            <td className="p-4 font-mono text-gray-400 text-right">{stat.total_trades}</td>
                                            <td className="p-4 text-right">
                                                <span className={`font-bold ${stat.win_rate >= 50 ? 'text-green-400' : 'text-yellow-500'}`}>{stat.win_rate}%</span>
                                            </td>
                                            <td className={`p-4 font-mono text-right font-bold ${stat.total_rr > 0 ? 'text-green-400' : stat.total_rr < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                                                {stat.total_rr > 0 ? '+' : ''}{stat.total_rr}R
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'history' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {history.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-500">No completed signals history available yet.</div>
                    ) : (
                        history.slice(0, 50).map(sig => (
                            <SignalCard key={sig.id} signal={sig} />
                        ))
                    )}
                </div>
            )}

        </main>
    );
};

export default Strategies;
